import { PageTitleDynamicKeys, PageTitlePrefixes } from '@legalzoom/constants';
import type { PageTitle, RouteHandle } from '../types';
import type {
  LoaderFunction,
  MetaArgs,
  UNSAFE_DataWithResponseInit,
} from 'react-router';
import { isPublicRoute } from './routes';
import { type RootLoader } from '../root.server';
import { isAuthenticatedLoaderData } from '../loaderData';
import type { WorkspaceMenuItem } from '@legalzoom/graphql/queries/account-menu.server';

const createPageTitle = (page?: string) =>
  `${page ?? ''}${PageTitleDynamicKeys.AccountName}${
    PageTitleDynamicKeys.Suffix
  }`;

const processPageTitle = (template: PageTitle, entityName?: string): string => {
  const titleWithMatchedKeys = Object.keys(PageTitleDynamicKeys)
    .filter((key) => {
      // Filter present patterns
      const value =
        PageTitleDynamicKeys[key as keyof typeof PageTitleDynamicKeys];
      return template.indexOf(value) > -1;
    })
    .reduce((accumulator, key) => {
      // Match the base pattern
      const value =
        PageTitleDynamicKeys[key as keyof typeof PageTitleDynamicKeys];
      // eslint-disable-next-line default-case
      switch (value) {
        case PageTitleDynamicKeys.AccountName:
          return accumulator.replace(
            value,
            entityName ? ` | ${entityName} | ` : ' | ',
          );
        case PageTitleDynamicKeys.Suffix:
          return accumulator.replace(
            value,
            `${
              accumulator.includes(PageTitleDynamicKeys.AccountName) ||
              accumulator === PageTitleDynamicKeys.Suffix
                ? ''
                : ' | '
            }LegalZoom`,
          );
      }
    }, template);

  return titleWithMatchedKeys;
};

type PageTitleProps<
  MatchesLoader extends LoaderFunction,
  DataLoader extends LoaderFunction,
> = {
  pathname: string;
  matches: MetaArgs<MatchesLoader, { root: RootLoader }>['matches'];
  data?: Awaited<ReturnType<DataLoader>> extends UNSAFE_DataWithResponseInit<
    infer T
  >
    ? T
    : Awaited<ReturnType<DataLoader>>;
};

const getPageTitle = <T extends LoaderFunction>({
  pathname,
  matches,
  data: loaderData,
}: PageTitleProps<T, RootLoader>) => {
  const title =
    (
      matches
        .filter((match) => !!(match?.handle as RouteHandle)?.pageTitle)
        .pop()?.handle as RouteHandle
    )?.pageTitle || createPageTitle(PageTitlePrefixes.Default);

  if (isAuthenticatedLoaderData(loaderData)) {
    const { workspace, user, menu } = loaderData;
    const { accountId, type } = workspace;
    const accountName =
      type === 'INDIVIDUAL'
        ? `${user?.firstName} ${user?.lastName} (Personal)`
        : (menu?.menuItems.find(
            (item: WorkspaceMenuItem) => item.accountId === accountId,
          )?.label ?? '');
    return processPageTitle(title, isPublicRoute(pathname) ? '' : accountName);
  }
  return processPageTitle(title);
};

const meta = <T extends LoaderFunction>({
  location,
  matches,
}: MetaArgs<T, { root: RootLoader }>) => {
  const rootLoaderData = matches.find(
    (match) => match.id === 'root',
  )?.loaderData;

  const title = getPageTitle<T>({
    pathname: location.pathname,
    matches: matches,
    data: rootLoaderData,
  });

  return [
    {
      title,
    },
  ];
};

const createMetaFunction = <T extends LoaderFunction>() => meta<T>;

export { createPageTitle, createMetaFunction, getPageTitle, processPageTitle };
